/**
 * Hydrates variant link box price/stock from /products/{handle}.js.
 * Fetch URLs are built ONLY from <a href> values already in the DOM.
 */

/** @type {Map<string, { price: number, available: boolean }>} */
const productCache = new Map();

/**
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const amount = (Number(cents) / 100).toFixed(2);
  const parts = amount.split('.');
  const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const fraction = parts[1];

  if (format.includes('{{amount_with_comma_separator}}')) {
    return format.replace('{{amount_with_comma_separator}}', `${whole},${fraction}`);
  }

  if (format.includes('{{amount_no_decimals}}')) {
    return format.replace('{{amount_no_decimals}}', whole);
  }

  return format.replace('{{amount}}', `${whole},${fraction}`);
}

/**
 * Build JSON endpoint URL from an exact href attribute value.
 * No encodeURIComponent, no handle parsing, no data attributes.
 *
 * @param {string | null | undefined} href
 * @returns {string | null}
 */
function productJsonUrlFromHref(href) {
  if (!href) return null;

  const path = href.split('?')[0].split('#')[0];
  if (!path) return null;

  if (path.endsWith('.js')) return path;
  return path + '.js';
}

/**
 * @param {HTMLElement} meta
 * @returns {string | null}
 */
function getProductJsonUrl(meta) {
  const item = meta.closest('.variant-box-item');
  if (!item) return null;

  // 1. Clickable variant box — use the same href the customer clicks
  const variantLink = item.querySelector('a.variant-box[href]');
  if (variantLink) {
    return productJsonUrlFromHref(variantLink.getAttribute('href'));
  }

  // 2. Sold-out box — hidden source link carries the canonical href
  const sourceLink = item.querySelector('a.variant-box__source-url[href]');
  if (sourceLink) {
    return productJsonUrlFromHref(sourceLink.getAttribute('href'));
  }

  // 3. Active (current) box — no <a>; this page's pathname is canonical
  if (item.querySelector('.variant-box--active')) {
    const path = window.location.pathname.split('?')[0].split('#')[0];
    if (!path) return null;
    if (path.endsWith('.js')) return path;
    return path + '.js';
  }

  return null;
}

/**
 * @param {string} jsonUrl
 * @returns {Promise<{ price: number, available: boolean } | null>}
 */
async function fetchLinkedProduct(jsonUrl) {
  if (productCache.has(jsonUrl)) {
    return productCache.get(jsonUrl) ?? null;
  }

  try {
    const response = await fetch(jsonUrl, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;

    const product = await response.json();
    if (!product?.id) return null;

    const data = {
      price: product.price,
      available: Boolean(product.available),
    };

    productCache.set(jsonUrl, data);
    return data;
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} meta
 * @param {string} moneyFormat
 */
async function hydrateMeta(meta, moneyFormat) {
  if (meta.dataset.hydrated === 'true') return;

  const jsonUrl = getProductJsonUrl(meta);
  if (!jsonUrl) return;

  const product = await fetchLinkedProduct(jsonUrl);
  if (!product) return;

  let priceEl = meta.querySelector('.variant-box__price');
  if (!priceEl) {
    priceEl = document.createElement('span');
    priceEl.className = 'variant-box__price';
    meta.appendChild(priceEl);
  }

  priceEl.textContent = formatMoney(product.price, moneyFormat);
  priceEl.hidden = false;

  let stockEl = meta.querySelector('.variant-box__stock');
  if (!stockEl) {
    stockEl = document.createElement('span');
    stockEl.className = 'variant-box__stock';
    meta.appendChild(stockEl);
  }

  stockEl.textContent = product.available ? 'Stokta var' : 'Tükendi';
  stockEl.hidden = false;
  meta.dataset.hydrated = 'true';

  const item = meta.closest('.variant-box-item');
  if (item && !product.available) {
    item.classList.add('variant-box-item--soldout');
  }
}

/**
 * @param {HTMLElement} root
 */
function hydrateVariantBoxGroup(root) {
  const moneyFormat = root.dataset.moneyFormat;
  if (!moneyFormat) return;

  root.querySelectorAll('.variant-box__meta').forEach((meta) => {
    if (!(meta instanceof HTMLElement)) return;
    if (meta.dataset.hydrated === 'true') return;
    void hydrateMeta(meta, moneyFormat);
  });
}

function initVariantBoxMeta() {
  document.querySelectorAll('.variant-box-group[data-money-format]').forEach((group) => {
    if (group instanceof HTMLElement) {
      hydrateVariantBoxGroup(group);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVariantBoxMeta);
} else {
  initVariantBoxMeta();
}
